import React, { useCallback, useEffect, useRef } from "react";
import {
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
} from "react-native";
import { batch, useDispatch, useSelector } from "react-redux";
import { getClientRequest } from "@microdoing/shared/services/signup/actions";
import { getUserRequest } from "@microdoing/shared/services/user/actions";
import {
  getClientId,
  getUser,
  getUserId,
} from "@microdoing/shared/services/user/selectors";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import {
  getHasCustomOnboarding,
  getRequesting,
} from "@microdoing/shared/services/client/selectors";
import UserStats from "./UserStats/UserStats";
import i18n from "@microdoing/shared/i18n";
import ProfileHeader from "./ProfileHeader";
import theme from "app/Theme/Theme";
import CogIcon from "app/components/Icons/CogIcon";
import HeaderFullScreen from "app/components/common/HeaderFullScreen";
import normalize from "app/utils/normalize";
import { Box } from "app/components/common";
import { getScrollTop } from "@microdoing/shared/services/nav/selectors";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsProfileScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { MbtiBlock } from "./Mbti/MbtiBlock";
import Badges from "./Badges";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import ProfileCertification from "./ProfileCertification";
import {
  getCampaignActionGoal,
  getCampaignEndDate,
  getTotalDoneSteps,
  getUserCampaignCertificate,
} from "@microdoing/shared/services/userCampaign/selectors";
import moment from "moment";
import {
  Incremental,
  IncrementalGroup,
} from "@th3rdwave/react-native-incremental";

const UserProfile = ({ navigation }) => {
  const user = useSelector(getUser);
  const userId = useSelector(getUserId);

  const {
    goals,
    hasGoals,
    skills,
    achievements,
    categories,
    firstName,
    lastName,
    bestStreak,
    bio,
    jobDescription,
    mbti,
    pulseSent,
    totalDoneActions,
    disc,
  } = user;

  const hasCustomOnboarding = useSelector(getHasCustomOnboarding);
  const clientId = useSelector(getClientId);
  const requesting = useSelector(getRequesting);
  const scrollViewRef = useRef(null);
  const shouldScrollTop = useSelector(getScrollTop);
  const endDate = useSelector(getCampaignEndDate);
  const totalDoneSteps = useSelector(getTotalDoneSteps);
  const campaignActionGoal = useSelector(getCampaignActionGoal);
  const certificationUrl = useSelector(getUserCampaignCertificate);

  const dispatch = useDispatch();

  const insets = useSafeAreaInsets();

  const refresh = useCallback(() => {
    batch(() => {
      getClientRequest(clientId);
      getUserRequest(userId);
    });
  }, [clientId, userId]);

  useEffect(() => {
    refresh();
    scrollTop();
    dispatch(changeNavField({ scrollTop: false }));
  }, [refresh, shouldScrollTop]);

  const scrollTop = () => {
    if (scrollViewRef && scrollViewRef.current) {
      scrollViewRef?.current?.scrollTo({ x: 0, y: 0, animated: true });
    }
  };

  const navigateToSettings = () => {
    navigation.navigate("UserProfileSettings");
  };

  const getProfileCompleteness = () => {
    let total = 1;
    const coef = hasCustomOnboarding ? total / 4 : total / 5;
    if (!firstName) {
      total -= coef;
    }
    if (!lastName) {
      total -= coef;
    }
    if (!jobDescription) {
      total -= coef;
    }
    if (!bio) {
      total -= coef;
    }
    if (!hasCustomOnboarding) {
      if (!mbti) {
        total -= coef;
      }
    }
    return total;
  };

  const getTotalSkillsPoints = useCallback(() => {
    const total = skills.reduce((prev, curr) => {
      return prev + curr.experience;
    }, 0);

    return total;
  }, [skills]);

  if (isWebDesktop)
    return (
      <WebDesktopView breadcrumbsPath={breadCrumbsProfileScreen}>
        <ScrollView
          refreshControl={
            <RefreshControl refreshing={requesting} onRefresh={refresh} />
          }
          ref={scrollViewRef}
          style={{ height: "100%", flex: 1, backgroundColor: "transparent" }}
          contentContainerStyle={styles.scrollViewDesktop}
        >
          <Box flex={1}>
            <ProfileHeader
              bio={bio}
              mbti={mbti}
              disc={disc}
              jobDescription={jobDescription}
              name={`${firstName} ${lastName}`}
              streak={bestStreak}
              pulseSent={pulseSent}
              actions={totalDoneActions}
              navigation={navigation}
              progress={getProfileCompleteness()}
              picture={user.picture}
            />
            {(moment(endDate).isBefore(moment(), "day") ||
              totalDoneSteps >= campaignActionGoal) && <ProfileCertification />}
          </Box>
          <Box flex={1}>
            <MbtiBlock />
            <UserStats
              skills={skills}
              goals={goals}
              hasGoals={hasGoals}
              categories={categories}
              achievements={achievements}
              totalSkillsPoints={getTotalSkillsPoints()}
            />
          </Box>
        </ScrollView>
      </WebDesktopView>
    );

  return (
    <Box flex={1} style={{ paddingTop: insets.top }}>
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={requesting} onRefresh={refresh} />
        }
        ref={scrollViewRef}
        style={styles.scrollViewContainer}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[styles.scrollView]}
      >
        <StatusBar backgroundColor={theme.colors["neutral.75"]} />
        <Box
          paddingHorizontal="sm"
          paddingTop="xs"
          marginBottom="s"
          alignItems="center"
          flexDirection="column"
          testID="profile-screen"
        >
          <HeaderFullScreen
            title={i18n.t("tabBar-profile-title")}
            rightIcon={<CogIcon height={normalize(30)} width={normalize(30)} />}
            callbackOnPressRight={navigateToSettings}
          />
          <Box
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight={100}
            width="100%"
          >
            <ProfileHeader
              bio={bio}
              mbti={mbti}
              disc={disc}
              jobDescription={jobDescription}
              name={`${firstName} ${lastName}`}
              streak={bestStreak}
              pulseSent={pulseSent}
              actions={totalDoneActions}
              navigation={navigation}
              progress={getProfileCompleteness()}
              picture={user.picture}
            />
            <MbtiBlock />
            <IncrementalGroup>
              <Incremental>
                {(moment(endDate).isBefore(moment(), "day") ||
                  totalDoneSteps >= campaignActionGoal) &&
                  !!certificationUrl && <ProfileCertification />}
              </Incremental>
              <Incremental>
                <UserStats
                  skills={skills}
                  goals={goals}
                  hasGoals={hasGoals}
                  categories={categories}
                  totalSkillsPoints={getTotalSkillsPoints()}
                />
              </Incremental>
              <Incremental>
                <Badges achievements={achievements} />
              </Incremental>
            </IncrementalGroup>
          </Box>
        </Box>
      </ScrollView>
    </Box>
  );
};

const styles = {
  scrollView: {
    paddingBottom: 40,
  },
  scrollViewDesktop: {
    paddingTop: isWebDesktop ? 60 : 50,
    width: "100%",
    justifyContent: "center",
    alignItems: "flex-start",
    display: "flex",
    flexDirection: isWebDesktop ? "row" : "column",
    columnGap: isWebDesktop ? theme.spacing.xm : 0,
  },
  scrollViewContainer: {
    backgroundColor: theme.colors["neutral.75"],
    width: "100%",
  },
};

export default UserProfile;
